import React, { FC, useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMount } from "react-use";

import { AddressInfoSuccessImage } from "@/assets/images";
import { SelectInput } from "@/components/atoms";
import { StandardLayout } from "@/components/layouts";
import { SuccessPage } from "@/components/organisms";
import { mapPolicyholderDropdownOption } from "@/components/organisms/PolicyholderDropdown/utils";
import { referenceApi, requestApi } from "@/shared/apis";
import { StaticTablePath } from "@/shared/apis/staticTablePath";
import { ChangeAddressPageState } from "@/shared/domain";
import { useAuth, usePolicyholders, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import AddressBox from "../components/AddressBox/AddressBox";
import RequestAddressChangeForm from "../components/RequestAddressChangeForm/RequestAddressChangeForm";
import { StyledRequestChangeAddressPage } from "./styled";

export interface FormValues {
    policyholderId: number;
}

const ChangeAddressPage: FC = () => {
    const {
        state: { policyholderId },
    } = useRouterHook<ChangeAddressPageState>();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const [showSuccessInfo, setShowSuccessInfo] = useState(false);
    const { getPolicyholders, isUserInPolicyholders } = usePolicyholders();
    const { incUsageCount } = useUserFeedback();

    const policyholders = getPolicyholders();

    const defaultPolicyholderId =
        policyholderId ||
        (isUserInPolicyholders() && user.policyHolderId) ||
        (policyholders.length && policyholders[0].policyHolderId);

    const { control, watch, setValue } = useForm<FormValues>({
        mode: "onBlur",
        defaultValues: { policyholderId: defaultPolicyholderId },
    });
    const watchPolicyholderId = Number(watch("policyholderId"));

    const policyholderIbpAclId =
        policyholders.find((policyholder) => policyholder.policyHolderId === watchPolicyholderId)?.accessRights
            ?.accessControlListId || user.accessControlListId;

    const { data: address } = useQuery(
        ["address", { policyholderId: watchPolicyholderId }],
        () => requestApi.getAddress(watchPolicyholderId, policyholderIbpAclId),
        {
            enabled: !!watchPolicyholderId,
        },
    );

    const { data: relations } = useQuery(
        ["relations", { policyholderId: watchPolicyholderId }],
        () => requestApi.getRelations(watchPolicyholderId),
        {
            enabled: !!watchPolicyholderId,
            suspense: false,
        },
    );
    const { options: countries } = referenceApi.useStaticQuery(StaticTablePath.Country);
    const { mutate: requestAddressChange } = useMutation(requestApi.requestAddressChange, {
        onSuccess: () => {
            queryClient.invalidateQueries("requestsPaginated");
            queryClient.invalidateQueries("address");
        },
    });

    useMount(() => {
        incUsageCount();
        tagManager.pushAddressChangeBeginEvent();
    });

    useEffect(() => {
        setValue("policyholderId", defaultPolicyholderId);
    }, [defaultPolicyholderId]);

    const [currentAddress, pendingAddress] = [address?.currentAddress, address?.pendingAddress];

    const handleSubmit = (formFields: MyBrokerApi.AddressChangeRequest) => {
        try {
            // send request to server
            requestAddressChange({
                policyholderId: watchPolicyholderId,
                body: formFields,
                params: {
                    ibpAclId: policyholderIbpAclId,
                },
            });

            // Tag Manager
            tagManager.pushAddressChangeSentEvent();

            // show success message
            setShowSuccessInfo(true);
        } catch (e) {
            tagManager.pushAddressChangeSentEvent(false);
            throw e;
        }
    };

    if (showSuccessInfo) {
        return (
            <StandardLayout title={t("PageTitles.AddressChange")}>
                <SuccessPage
                    icon={<AddressInfoSuccessImage />}
                    message={t("User.RequestAddressChange.InfoSuccess.Text")}
                />
            </StandardLayout>
        );
    }
    const identifiablePolicyholders = mapPolicyholderDropdownOption(policyholders);
    return (
        <StandardLayout title={t("PageTitles.AddressChange")} disableSearch>
            <StyledRequestChangeAddressPage>
                {policyholders?.length > 1 && (
                    <div className="policyholderListAddressForm">
                        <div className="subTitle">{t("User.RequestAddressChange.Policyholder.Text")}</div>
                        <SelectInput
                            name="policyholderId"
                            control={control}
                            label={t("Common.Policyholder")}
                            fullWidth={false}
                            TextFieldProps={{
                                inputProps: {
                                    style: { width: "auto" },
                                },
                            }}
                            required
                            options={identifiablePolicyholders}
                        />
                    </div>
                )}
                {currentAddress && <AddressBox currentAddress={currentAddress} />}
                <div className="subTitle">{t("User.RequestAddressChange.SubTitle")}</div>
                <RequestAddressChangeForm
                    pendingAddressChange={pendingAddress}
                    relations={relations?.currentRelations}
                    onSubmit={handleSubmit}
                    countries={countries.sortBy("name")}
                />
            </StyledRequestChangeAddressPage>
        </StandardLayout>
    );
};

export default ChangeAddressPage;
